import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const benefits = [
  {
    icon: 'ri-shield-check-line',
    title: 'HIPAA Compliant',
    desc: 'Meets strict healthcare data privacy requirements for medical facilities.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Certificate of Destruction',
    desc: 'Official documentation provided for every device destroyed.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Witness Destruction',
    desc: 'Watch your drives being destroyed in person at our facility.',
  },
  {
    icon: 'ri-truck-line',
    title: 'On-Site Service',
    desc: 'We come to your location with mobile shredding equipment.',
  },
  {
    icon: 'ri-lock-line',
    title: 'Chain of Custody',
    desc: 'Every drive tracked by serial number from pickup to destruction.',
  },
  {
    icon: 'ri-recycle-line',
    title: 'Zero Landfill',
    desc: 'All shredded materials responsibly recycled after destruction.',
  },
];

const deviceTypes = [
  { icon: 'ri-server-line', label: 'Computer Server\nHard Drive 3.5"' },
  { icon: 'ri-computer-line', label: 'Laptop Hard\nDrive 2.5"' },
  { icon: 'ri-hard-drive-2-line', label: 'External\nHard Drive' },
  { icon: 'ri-smartphone-line', label: 'Cell Phone\n& Tablets' },
  { icon: 'ri-database-2-line', label: 'SSD Solid\nState Drives' },
  { icon: 'ri-usb-line', label: 'USB Flash\nDrives' },
  { icon: 'ri-disc-line', label: 'CDs, DVDs\n& Tapes' },
  { icon: 'ri-sd-card-line', label: 'Memory\nCards' },
];

const processSteps = [
  {
    num: '1',
    title: 'Schedule Pickup or Drop-Off',
    desc: 'Call (213) 600-1000 or request service online. We offer free business pickup across LA County.',
  },
  {
    num: '2',
    title: 'Chain of Custody Logging',
    desc: 'Each drive is logged by serial number and model. You receive a detailed inventory list.',
  },
  {
    num: '3',
    title: 'Physical Destruction',
    desc: 'Drives are shredded by our industrial hard drive destroyer. No data recovery possible.',
  },
  {
    num: '4',
    title: 'Certificate Issued',
    desc: 'Receive a Certificate of Destruction with serial numbers for compliance and records.',
  },
];

const industries = [
  { icon: 'ri-hospital-line', name: 'Healthcare', desc: 'HIPAA-compliant destruction for hospitals and clinics' },
  { icon: 'ri-bank-line', name: 'Financial', desc: 'SEC and FINRA compliant for banks and investment firms' },
  { icon: 'ri-government-line', name: 'Government', desc: 'DoD 5220.22-M standards for agencies and contractors' },
  { icon: 'ri-building-4-line', name: 'Corporate', desc: 'Protect trade secrets and client data for businesses' },
  { icon: 'ri-graduation-cap-line', name: 'Education', desc: 'FERPA-compliant for schools and universities' },
  { icon: 'ri-store-line', name: 'Retail', desc: 'PCI-DSS compliant for payment card industry' },
];

const faqs = [
  {
    q: 'What is hard drive destruction?',
    a: 'Hard drive destruction is the physical shredding or crushing of hard drives to ensure data can never be recovered. Unlike software wiping, physical destruction guarantees 100% data elimination.',
  },
  {
    q: 'Why is hard drive destruction necessary?',
    a: 'Discarded computers and hard drives are goldmines for identity thieves. Even "deleted" data can be recovered. Physical destruction is the only way to guarantee data security and comply with privacy laws like HIPAA, GLBA, and state data breach laws.',
  },
  {
    q: 'Do you provide a Certificate of Destruction?',
    a: 'Yes. Every hard drive destroyed receives an official Certificate of Destruction documenting the serial numbers, date of destruction, and method used. This certificate satisfies audit and compliance requirements.',
  },
  {
    q: 'Can I watch my hard drives being destroyed?',
    a: 'Absolutely. We offer witness destruction at our Los Angeles facility. You can watch your drives being shredded and receive your certificate immediately.',
  },
  {
    q: 'Do you offer on-site hard drive shredding?',
    a: 'Yes. For large quantities or high-security needs, we can bring our mobile shredding truck to your location anywhere in Los Angeles County.',
  },
  {
    q: 'What types of devices can you destroy?',
    a: 'We destroy all types of data storage devices: 3.5" and 2.5" hard drives, SSDs, external drives, USB flash drives, cell phones, tablets, CDs/DVDs, and magnetic tapes.',
  },
  {
    q: 'How much does hard drive destruction cost?',
    a: 'Pricing depends on quantity and service type. Drop-off starts at competitive rates, and we offer free pickup for businesses with larger quantities. Call (213) 600-1000 for a quick quote.',
  },
  {
    q: 'Are you certified for data destruction?',
    a: 'Yes. Greenly is CalRecycle certified (CEWD-127531) and follows NIST 800-88 and DoD 5220.22-M guidelines for media sanitization.',
  },
];

const HardDriveDestructionPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

  return (
    <>
      <title>Hard Drive Destruction Los Angeles | Certified Data Destruction &amp; Shredding | Greenly</title>
      <meta name="description" content="Certified hard drive shredding & data destruction in Los Angeles. HIPAA compliant. Certificate of Destruction provided. On-site & drop-off. CalRecycle CEWD-127531. Call (213) 600-1000." />
      <meta name="keywords" content="hard drive destruction Los Angeles, data destruction Los Angeles, hard drive shredding LA, HIPAA data destruction Los Angeles, certified data destruction" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/hard-drive-destruction`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${SITE_URL}/hard-drive-destruction#webpage`,
            "url": `${SITE_URL}/hard-drive-destruction`,
            "name": "Hard Drive Destruction Los Angeles | Certified Data Destruction & Shredding | Greenly",
            "description": "Certified hard drive destruction & data shredding in Los Angeles. HIPAA compliant, Certificate of Destruction provided. On-site & drop-off. CalRecycle CEWD-127531. Call (213) 600-1000.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "Hard Drive Destruction", "item": `${SITE_URL}/hard-drive-destruction` }
              ]
            }
          },
          {
            "@type": "Service",
            "@id": `${SITE_URL}/hard-drive-destruction#service`,
            "name": "Hard Drive Destruction Los Angeles",
            "description": "Certified physical hard drive destruction and data shredding in Los Angeles. HIPAA compliant, Certificate of Destruction provided. On-site and drop-off available.",
            "url": `${SITE_URL}/hard-drive-destruction`,
            "provider": { "@id": `${SITE_URL}/#business` },
            "areaServed": { "@type": "City", "name": "Los Angeles" },
            "serviceType": "Data Destruction",
            "offers": {
              "@type": "Offer",
              "price": "10.00",
              "priceCurrency": "USD",
              "description": "Hard Drive Shredding with Certificate of Destruction — $10 each"
            }
          },
          {
            "@type": "FAQPage",
            "@id": `${SITE_URL}/hard-drive-destruction#faq`,
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          }
        ]
      })}</script>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gray-900 pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Industrial%20hard%20drive%20shredding%20machine%20destroying%20computer%20hard%20drives%2C%20metal%20shavings%20and%20destroyed%20electronics%2C%20dramatic%20lighting%20on%20machinery%2C%20professional%20data%20destruction%20facility%2C%20dark%20industrial%20background%20with%20green%20accent%20lighting%2C%20close-up%20of%20shredding%20mechanism%20in%20action%2C%20high%20security%20data%20destruction%20concept&width=1400&height=600&seq=hddhero001&orientation=landscape"
              alt="Hard drive shredding machine destroying data storage devices"
              className="w-full h-full object-cover object-center opacity-40"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Hard Drive Destruction' }]} />
            <div className="max-w-2xl mt-8">
              <div className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-shield-check-line"></i>
                Certified Data Destruction
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                Hard Drive Destruction
                <span className="block text-[#74C69D]">in Los Angeles</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Secure, certified physical destruction of hard drives and electronic media. 
                Protect your sensitive data with HIPAA-compliant shredding services across 
                Los Angeles County.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:+12136001000"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-fill"></i>
                  (213) 600-1000
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-calendar-check-line"></i>
                  Request Service
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <section className="bg-[#1B6B3A] py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-white text-xs md:text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-award-line text-[#74C69D]"></i>
                <span className="font-semibold">CalRecycle Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-file-list-3-line text-[#74C69D]"></i>
                <span className="font-semibold">Certificate Provided</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-[#74C69D]"></i>
                <span className="font-semibold">Los Angeles Based</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-[#74C69D]"></i>
                <span className="font-semibold">HIPAA Compliant</span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Hard Drive Destruction */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
              <div className="w-full lg:w-1/2">
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                  Data Security
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5 leading-tight">
                  Why is Hard Drive Destruction Necessary?
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Discarded or donated computers, copiers, and media disks are a principal source 
                  of information for identity thieves. These items should not be viewed as trash 
                  but rather as goldmines of confidential data.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  The majority of US recycling companies ship electronic waste or "e-waste" overseas. 
                  Identity thieves are able to buy and sift through hundreds of hard drives and media 
                  disks at a time, pulling social security numbers, bank account details, birth 
                  certificates, online passwords, passport photos, and addresses.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  <strong className="text-gray-900">Physical destruction is the only 100% secure method.</strong> Software wiping 
                  can leave traces. Degaussing doesn't work on SSDs. Only shredding guarantees 
                  complete data elimination.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-bold px-5 py-2.5 rounded-md hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-shield-check-line"></i>
                    Secure Your Data
                  </Link>
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src="/images/Hard Drive Destruction.jpg"
                    alt="Destroyed hard drives and electronic media after secure shredding"
                    className="w-full h-[300px] md:h-[400px] object-cover object-center"
                    loading="lazy"
                    width={700}
                    height={500}
                  />
                </div>
                <div className="mt-4 bg-[#F4FAF7] rounded-xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">100% Data Security Guarantee</div>
                    <div className="text-gray-500 text-xs">No data recovery possible after physical destruction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                Our Promise
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Why Choose Greenly for Data Destruction
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((b) => (
                <div key={b.title} className="bg-white rounded-xl p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl mb-4">
                    <i className={`${b.icon} text-[#1B6B3A] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{b.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Device Types */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                What We Destroy
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                All Types of Data Storage Devices
              </h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
                We physically destroy all electronic media — not just hard drives. 
                If it stores data, we can destroy it.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {deviceTypes.map((d) => (
                <div key={d.label} className="bg-[#F4FAF7] rounded-xl p-5 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1B6B3A] rounded-xl mx-auto mb-3">
                    <i className={`${d.icon} text-white text-xl`}></i>
                  </div>
                  <div className="text-xs font-bold text-gray-900 leading-snug whitespace-pre-line">
                    {d.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-14 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#74C69D] text-xs font-bold uppercase tracking-widest mb-2">
                Our Process
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white">
                How Hard Drive Destruction Works
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, i) => (
                <div key={step.num} className="relative">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-lg">
                        <span className="text-white font-bold text-sm">{step.num}</span>
                      </div>
                      {i < processSteps.length - 1 && (
                        <div className="hidden lg:flex flex-1 h-px bg-white/20"></div>
                      )}
                    </div>
                    <h3 className="font-bold text-white text-sm mb-2">{step.title}</h3>
                    <p className="text-gray-400 text-xs leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                Industries Served
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Trusted Across All Industries
              </h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
                We understand the unique compliance requirements of different industries 
                and tailor our services accordingly.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {industries.map((ind) => (
                <div key={ind.name} className="border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A] transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl mb-3">
                    <i className={`${ind.icon} text-[#1B6B3A] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{ind.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{ind.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Residential Section */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="w-full lg:w-1/2">
                <img
                  src="/images/Residential Hard Drive Shredding.jpg"
                  alt="Residential hard drive destruction for Los Angeles homeowners"
                  className="w-full h-[280px] md:h-[350px] object-cover object-center rounded-2xl"
                  loading="lazy"
                  width={600}
                  height={400}
                />
              </div>
              <div className="w-full lg:w-1/2">
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                  For Homeowners
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
                  Residential Hard Drive Shredding
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  We also offer hard drive destruction services for residential customers. 
                  Our residential services help keep our customer's private information 
                  confidential and secure, protecting individuals from identity theft and 
                  other information thieves.
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    'Protect personal photos, documents, and financial records',
                    'Safe disposal of old family computers and laptops',
                    'Certificate of destruction for your records',
                    'Drop-off at our LA facility or schedule pickup',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#1B6B3A] shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-bold px-5 py-2.5 rounded-md hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-home-smile-line"></i>
                  Residential Service
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                Common Questions
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Hard Drive Destruction FAQs
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Securely Destroy Your Hard Drives?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Don't risk a data breach. Get certified hard drive destruction from Los Angeles' 
              trusted e-waste recycler. Call now or request service online.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>
                Call (213) 600-1000
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-calendar-check-line"></i>
                Request Service Online
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default HardDriveDestructionPage;